import { Fragment } from 'wp.element';
import { __ } from 'wp.i18n';
import { ToggleControl, RangeControl, PanelBody } from 'wp.components';
import { InspectorControls, RichText, useBlockProps } from 'wp.blockEditor';
import classNames from 'classnames';

import useUniqueId from '../../hooks/useUniqueId';
import ProgressBarStyle from './style';
import ResponsiveControl from '../../components/controls/responsive-control/ResponsiveControl';
import MarginControls from '../../components/controls/margin-controls';
import FontSizePickerLabel from '../../components/controls/text-controls/FontSizePickerLabel';
import getBlockId from '../../util/getBlockId';
import { getBorderCSSValue } from '../../components/controls/border-controls/helpers';
import BorderControls from '../../components/controls/border-controls';
import { getBoxShadowCSSValue } from '../../components/controls/box-shadow-controls/helpers';
import BoxShadowControls from '../../components/controls/box-shadow-controls';
import PopoverColorControl from '../../components/controls/advanced-color-control/PopoverColorControl';
import BreakpointVisibilityControl from '../../components/controls/breakpoint-visibility-control';
import { getBreakpointVisibilityClassNames } from '../../components/controls/breakpoint-visibility-control/helpers';
import { getAuthVisibilityClasses } from '../../components/controls/auth-visibility-control/helpers';
import AuthVisibilityControl from '../../components/controls/auth-visibility-control';
import AnimationControls from '../../components/controls/animation-controls/AnimationControls';

const ProgressBarEdit = ({
  attributes,
  isSelected,
  className,
  setAttributes,
  clientId,
}) => {
  useUniqueId({ attributes, setAttributes, clientId });

  const {
    uniqueId,
    title,
    innerTitle,
    percentage,
    displayPercentage,
    barBackgroundColor,
    progressBackgroundColor,
    backgroundColor,
    textColor,
    titleFontSize,
    titleTextColor,
    innerTitleFontSize,
    titleBottomMargin,
    blockBreakpointVisibility,
    blockAuthVisibility,
    height,
  } = attributes;

  const blockId = getBlockId(uniqueId);

  const blockProps = useBlockProps({
    id: blockId,
    className: classNames(
      className,
      blockId,
      getBreakpointVisibilityClassNames(blockBreakpointVisibility),
      getAuthVisibilityClasses(blockAuthVisibility),
    ),
    style: {
      backgroundColor: backgroundColor || undefined,
      ...getBorderCSSValue({ attributes }),
      ...getBoxShadowCSSValue({ attributes }),
    },
  });

  return (
    <Fragment>
      <div {...blockProps}>
        <ProgressBarStyle attributes={attributes} />

        <RichText
          key="title"
          tagName="p"
          value={title}
          onChange={value => setAttributes({ title: value })}
          className="wp-block-gutenbee-progress-bar-title"
          placeholder={__('Write title…')}
          style={{
            color: titleTextColor || undefined,
            marginBottom:
              titleBottomMargin == null ? undefined : `${titleBottomMargin}px`,
          }}
        />

        <div
          className="wp-block-gutenbee-progress-bar-outer"
          style={{
            backgroundColor: barBackgroundColor || undefined,
          }}
        >
          <div
            className="wp-block-gutenbee-progress-bar-inner"
            style={{
              width: `${percentage}%`,
              backgroundColor: progressBackgroundColor || undefined,
              color: textColor || undefined,
            }}
          >
            <RichText
              key="innerTitle"
              tagName="span"
              value={innerTitle}
              onChange={value => setAttributes({ innerTitle: value })}
              className="wp-block-gutenbee-progress-bar-inner-title"
              placeholder={__('Write inner title…')}
            />

            {displayPercentage && (
              <span className="wp-block-gutenbee-progress-bar-percentage">
                {percentage}%
              </span>
            )}
          </div>
        </div>
      </div>

      {isSelected && (
        <InspectorControls>
          <PanelBody>
            <RangeControl
              label={__('Percentage')}
              min={1}
              max={100}
              value={percentage}
              onChange={value => setAttributes({ percentage: value })}
              step={1}
              __next40pxDefaultSize
              __nextHasNoMarginBottom
            />

            <ToggleControl
              label={__('Display percentage')}
              checked={displayPercentage}
              onChange={value => setAttributes({ displayPercentage: value })}
              __nextHasNoMarginBottom
            />

            <RangeControl
              label={__('Title Bottom Margin')}
              value={titleBottomMargin}
              onChange={value => {
                setAttributes({
                  titleBottomMargin: value != null ? value : undefined,
                });
              }}
              allowReset
              min={0}
              max={200}
              __next40pxDefaultSize
              __nextHasNoMarginBottom
            />

            <ResponsiveControl>
              {breakpoint => (
                <FontSizePickerLabel
                  label={__('Title Font Size')}
                  value={
                    titleFontSize[breakpoint] != null &&
                    titleFontSize[breakpoint] !== ''
                      ? titleFontSize[breakpoint]
                      : undefined
                  }
                  onChange={value =>
                    setAttributes({
                      titleFontSize: {
                        ...titleFontSize,
                        [breakpoint]: value != null ? value : undefined,
                      },
                    })
                  }
                />
              )}
            </ResponsiveControl>

            <ResponsiveControl>
              {breakpoint => (
                <FontSizePickerLabel
                  label={__('Bar Text Font Size')}
                  value={
                    innerTitleFontSize[breakpoint] != null &&
                    innerTitleFontSize[breakpoint] !== ''
                      ? innerTitleFontSize[breakpoint]
                      : undefined
                  }
                  onChange={value =>
                    setAttributes({
                      innerTitleFontSize: {
                        ...innerTitleFontSize,
                        [breakpoint]: value != null ? value : undefined,
                      },
                    })
                  }
                />
              )}
            </ResponsiveControl>

            <ResponsiveControl>
              {breakpoint => (
                <Fragment>
                  <RangeControl
                    label={__('Bar Height (px)')}
                    min={30}
                    max={200}
                    value={height[breakpoint]}
                    allowReset
                    onChange={value => {
                      setAttributes({
                        height: {
                          ...height,
                          [breakpoint]: value != null ? value : '',
                        },
                      });
                    }}
                    step={1}
                    __next40pxDefaultSize
                    __nextHasNoMarginBottom
                  />
                </Fragment>
              )}
            </ResponsiveControl>
          </PanelBody>

          <PanelBody title={__('Block Appearance')} initialOpen={false}>
            <PopoverColorControl
              label={__('Title Text Color')}
              value={titleTextColor || ''}
              defaultValue={titleTextColor || ''}
              onChange={value => setAttributes({ titleTextColor: value })}
            />

            <PopoverColorControl
              label={__('Bar Text Color')}
              value={textColor || ''}
              defaultValue={textColor || ''}
              onChange={value => setAttributes({ textColor: value })}
            />

            <PopoverColorControl
              label={__('Progress Background Color')}
              value={progressBackgroundColor || ''}
              defaultValue={progressBackgroundColor || ''}
              onChange={value =>
                setAttributes({ progressBackgroundColor: value })
              }
            />

            <PopoverColorControl
              label={__('Bar Background Color')}
              value={barBackgroundColor || ''}
              defaultValue={barBackgroundColor || ''}
              onChange={value => setAttributes({ barBackgroundColor: value })}
            />

            <PopoverColorControl
              label={__('Block Background Color')}
              value={backgroundColor || ''}
              defaultValue={backgroundColor || ''}
              onChange={value => setAttributes({ backgroundColor: value })}
            />

            <BorderControls
              attributes={attributes}
              setAttributes={setAttributes}
            />

            <BoxShadowControls
              attributes={attributes}
              setAttributes={setAttributes}
            />

            <ResponsiveControl>
              {breakpoint => (
                <MarginControls
                  label={__('Padding (px)')}
                  attributeKey="blockPadding"
                  attributes={attributes}
                  setAttributes={setAttributes}
                  breakpoint={breakpoint}
                />
              )}
            </ResponsiveControl>

            <ResponsiveControl>
              {breakpoint => (
                <MarginControls
                  label={__('Margin (px)')}
                  attributeKey="blockMargin"
                  attributes={attributes}
                  setAttributes={setAttributes}
                  breakpoint={breakpoint}
                />
              )}
            </ResponsiveControl>
          </PanelBody>
          <PanelBody title={__('Visibility Settings')} initialOpen={false}>
            <BreakpointVisibilityControl
              values={blockBreakpointVisibility}
              onChange={values => {
                setAttributes({
                  blockBreakpointVisibility: values,
                });
              }}
            />

            <AuthVisibilityControl
              values={blockAuthVisibility}
              onChange={values => {
                setAttributes({
                  blockAuthVisibility: values,
                });
              }}
            />
          </PanelBody>

          {__GUTENBEE_SETTINGS__.plugin.settings[
            'active_animation-controls'
          ] && (
            <PanelBody
              icon={!!attributes.animation?.type && 'saved'}
              title={__('Animation')}
              initialOpen={false}
            >
              <AnimationControls
                attributes={{
                  ...attributes.animation,
                  duration:
                    attributes.animation?.duration !== undefined &&
                    attributes.animation?.duration !== ''
                      ? Number(attributes.animation.duration)
                      : undefined,
                  delay:
                    attributes.animation?.delay !== undefined &&
                    attributes.animation?.delay !== ''
                      ? Number(attributes.animation.delay)
                      : undefined,
                }}
                setAttributes={setAttributes}
              />
            </PanelBody>
          )}
        </InspectorControls>
      )}
    </Fragment>
  );
};

export default ProgressBarEdit;
